/** Step by step form
 * Transforms a form (.steps) into a step-by-step wizard.
 *
 * Navigation:
 *  - Forward: only via the .total submit button (the form's main CTA).
 *  - Backward: only via clicking a previous (completed) step in the progress bar.
 *  - No horizontal scroll, no swipe, no inline next/prev buttons.
 *  - On the last step, the .total button submits the form.
 */

import {ui_is_small_screen, ui_scroll_to, ui_shake} from './util.js';

document.addEventListener('DOMContentLoaded', function () {

    document.querySelectorAll('.steps').forEach(globalStepperContainer => {

        const stepEls = Array.from(globalStepperContainer.querySelectorAll('.step'));
        const stepsCount = stepEls.length;
        if (stepsCount === 0) return;

        /* Sliding rail wrapper */
        const container = document.createElement('div');
        container.classList.add('step-container');
        globalStepperContainer.prepend(container);

        const rail = document.createElement('div');
        rail.classList.add('step-scrollbar');
        rail.style.width = `${stepsCount * 100}%`;
        container.appendChild(rail);

        stepEls.forEach((step, index) => {
            rail.appendChild(step);
            step.style.width = `${100 / stepsCount}%`;

            if (index === 0) {
                step.classList.add('active');
            }

            /* Submit on Enter: behave like clicking the .total button */
            step.querySelectorAll('input, select, textarea').forEach(input => {
                input.addEventListener('keydown', function (event) {
                    if (event.key === 'Enter' && input.tagName !== 'TEXTAREA') {
                        event.preventDefault();
                        advanceFromTotal();
                    }
                });
            });
        });

        /* Bottom progress bar with labels.
         * Labels come from data-label on each .step (fallback: index). */
        const progress = document.createElement('ul');
        progress.classList.add('step-progress');
        let currentIndex = 0;
        stepEls.forEach((step, index) => {
            const li = document.createElement('li');
            const label = step.dataset.label || `Étape ${index + 1}`;
            const num = document.createElement('span');
            num.classList.add('num');
            num.textContent = String(index + 1);
            const text = document.createElement('span');
            text.classList.add('label');
            text.textContent = label;
            li.appendChild(num);
            li.appendChild(text);

            li.addEventListener('click', function () {
                /* Only allow going back to a previously completed step. */
                if (index < currentIndex) {
                    gotoStep(index);
                }
            });

            if (index === 0) {
                li.classList.add('active');
            }
            progress.appendChild(li);
        });

        /* Reparent progress under .booking-form so it sits above .total */
        const formEl = globalStepperContainer.closest('.booking-form');
        const totalEl = formEl?.querySelector('.total');
        /* Bloc « Réservation » : sur petit écran il est empilé sous le calendrier. */
        const scrollTarget = globalStepperContainer.closest('.bl') ?? globalStepperContainer;
        if (formEl) {
            formEl.appendChild(progress);
        } else {
            container.appendChild(progress);
        }

        /* Keep --total-height and --progress-height in sync so the form
         * reserves room for the absolute bars at the bottom. */
        if (formEl && totalEl) {
            const syncSizes = () => {
                formEl.style.setProperty('--total-height', `${totalEl.offsetHeight}px`);
                formEl.style.setProperty('--progress-height', `${progress.offsetHeight}px`);
            };
            syncSizes();
            if (typeof ResizeObserver !== 'undefined') {
                const ro = new ResizeObserver(syncSizes);
                ro.observe(totalEl);
                ro.observe(progress);
            }
            window.addEventListener('resize', syncSizes);
        }

        const applyTransform = () => {
            rail.style.transform = `translateX(-${currentIndex * (100 / stepsCount)}%)`;
        };
        applyTransform();

        /** Move to a step (validates forward navigation).
         * @param index {number}
         */
        const gotoStep = (index, options = {}) => {
            if (index < 0 || index >= stepsCount) return;
            if (index === currentIndex) return;

            const movingForward = index > currentIndex;

            if (index > currentIndex && !options.force) {
                const currentStep = stepEls[currentIndex];
                currentStep.querySelectorAll('input, select, textarea').forEach(input => {
                    if (!input.checkValidity()) {
                        input.classList.add('error');
                    } else {
                        input.classList.remove('error');
                    }
                });
                const firstError = currentStep.querySelector('.error');
                if (firstError !== null) {
                    ui_shake(firstError);
                    firstError.focus();
                    return;
                }

                /* Un écouteur peut suspendre la transition (envoi d'un mail, appel réseau…)
                 * en appelant preventDefault(), puis la reprendre via detail.resume(). */
                const leaveEvent = new CustomEvent('step:leave', {
                    cancelable: true,
                    detail: {
                        from: currentIndex,
                        to: index,
                        step: currentStep,
                        resume: () => gotoStep(index, {force: true})
                    }
                });
                (formEl ?? globalStepperContainer).dispatchEvent(leaveEvent);
                if (leaveEvent.defaultPrevented) return;
            }

            stepEls.forEach(s => s.classList.remove('active'));
            stepEls[index].classList.add('active');

            Array.from(progress.children).forEach((li, i) => {
                li.classList.toggle('active', i === index);
                li.classList.toggle('completed', i < index);
                li.classList.toggle('clickable', i < index);
            });

            currentIndex = index;
            formEl?.classList.toggle('is-last-step', currentIndex === stepsCount - 1);
            applyTransform();

            /* L'étape précédente a pu être défilée (ou, sur petit écran, la page entière
             * jusqu'au bouton de validation en bas) : la nouvelle étape se lit depuis son début. */
            stepEls[index].scrollTop = 0;
            if (ui_is_small_screen()) {
                ui_scroll_to(scrollTarget);
            }

            /* Étape validée : la saisie reprend sur le premier champ de la suivante.
             * preventScroll pour ne pas contrarier le défilement ci-dessus. */
            if (movingForward) {
                stepEls[index]
                    .querySelector('input:not([disabled]), select:not([disabled]), textarea:not([disabled])')
                    ?.focus({preventScroll: true});
            }
        };

        /* Wire the .total CTA: advance or submit on last step. */
        const totalButton = totalEl?.querySelector('.submit button');

        /* Sur la dernière étape (confirmation) il n'y a plus rien à envoyer :
         * la demande est partie à la sortie de l'étape précédente. */
        const advanceFromTotal = () => {
            if (currentIndex < stepsCount - 1) {
                gotoStep(currentIndex + 1);
            }
        };

        if (totalButton) {
            totalButton.setAttribute('type', 'button');
            totalButton.addEventListener('click', function (event) {
                event.preventDefault();
                advanceFromTotal();
            });
        }

        /* Initialize progress classes */
        Array.from(progress.children).forEach((li, i) => {
            li.classList.toggle('active', i === 0);
            li.classList.toggle('completed', false);
            li.classList.toggle('clickable', false);
        });
    });
});
